import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Download, Smartphone, Share, Plus, Apple, Chrome, Loader2, History, ShieldCheck, Sailboat, CheckCircle2,
} from "lucide-react";

function detectPlatform() {
  const ua = (navigator.userAgent || "").toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /android/.test(ua);
  const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
  return { isIOS, isAndroid, isStandalone };
}

function PwaInstall() {
  const [installable, setInstallable] = useState(!!window.__deferredPwaPrompt);
  const [installed, setInstalled] = useState(false);
  const { isIOS, isAndroid, isStandalone } = detectPlatform();

  useEffect(() => {
    const onAvail = () => setInstallable(true);
    const onInstalled = () => { setInstalled(true); setInstallable(false); };
    window.addEventListener("pwa-installable", onAvail);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("pwa-installable", onAvail);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    const ev = window.__deferredPwaPrompt;
    if (!ev) return;
    ev.prompt();
    const choice = await ev.userChoice;
    if (choice?.outcome === "accepted") {
      setInstalled(true);
    }
    window.__deferredPwaPrompt = null;
    setInstallable(false);
  };

  if (isStandalone || installed) {
    return (
      <div className="tk-card p-5" data-testid="pwa-already-installed">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#e7f5ee" }}>
            <CheckCircle2 className="w-5 h-5" style={{ color: "hsl(var(--success))" }} />
          </div>
          <div>
            <div className="font-display font-semibold">Uygulama yüklü</div>
            <p className="text-sm mt-1" style={{ color: "hsl(var(--text-2))" }}>
              Mavi Yolculuk '26'yı ana ekrandan açabilirsin. Tarayıcı çubuğu olmadan, tam ekran çalışır.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="tk-card p-5" data-testid="pwa-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#dbe9ff" }}>
            <Smartphone className="w-5 h-5" style={{ color: "#4992ff" }} />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">Telefonuna kur</div>
            <p className="text-sm mt-1" style={{ color: "hsl(var(--text-2))" }}>
              Uygulamayı ana ekrana ekle, internet kopsa bile son indirilen alışveriş listesi/menü görünsün.
            </p>
            {installable && (
              <button
                onClick={triggerInstall}
                data-testid="pwa-install-btn"
                className="tk-btn mt-3 inline-flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Hemen Yükle
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Platform-specific instructions */}
      {(isIOS || (!isIOS && !isAndroid)) && (
        <div className="tk-card p-5" data-testid="ios-instructions">
          <div className="flex items-center gap-2 mb-3">
            <Apple className="w-5 h-5" />
            <div className="font-display font-semibold">iPhone / iPad (Safari)</div>
          </div>
          <ol className="space-y-2 text-sm" style={{ color: "hsl(var(--text-1))" }}>
            <li className="flex items-start gap-2"><span className="mt-0.5 w-5 h-5 rounded-full text-[11px] font-semibold flex items-center justify-center" style={{ backgroundColor: "#dbe9ff", color: "#4992ff" }}>1</span><span>Safari'de bu sayfada iken alttaki <Share className="inline w-4 h-4 mx-0.5" />  <b>Paylaş</b> butonuna bas.</span></li>
            <li className="flex items-start gap-2"><span className="mt-0.5 w-5 h-5 rounded-full text-[11px] font-semibold flex items-center justify-center" style={{ backgroundColor: "#dbe9ff", color: "#4992ff" }}>2</span><span>Açılan menüden <b>Ana Ekrana Ekle</b> <Plus className="inline w-4 h-4 mx-0.5" /> seçeneğini seç.</span></li>
            <li className="flex items-start gap-2"><span className="mt-0.5 w-5 h-5 rounded-full text-[11px] font-semibold flex items-center justify-center" style={{ backgroundColor: "#dbe9ff", color: "#4992ff" }}>3</span><span>Sağ üstten <b>Ekle</b>'ye dokun. İkon ana ekranına gelir.</span></li>
          </ol>
          <p className="text-xs mt-3" style={{ color: "hsl(var(--text-2))" }}>
            Not: Chrome (iOS) "Ana Ekrana Ekle" özelliğini desteklemez. Bu adımları <b>Safari</b>'de yapman gerekir.
          </p>
        </div>
      )}

      {(isAndroid || (!isIOS && !isAndroid)) && (
        <div className="tk-card p-5" data-testid="android-instructions">
          <div className="flex items-center gap-2 mb-3">
            <Chrome className="w-5 h-5" />
            <div className="font-display font-semibold">Android (Chrome)</div>
          </div>
          <ol className="space-y-2 text-sm" style={{ color: "hsl(var(--text-1))" }}>
            <li className="flex items-start gap-2"><span className="mt-0.5 w-5 h-5 rounded-full text-[11px] font-semibold flex items-center justify-center" style={{ backgroundColor: "#dbe9ff", color: "#4992ff" }}>1</span><span>Chrome'da sağ üstteki <b>⋮</b> menüsüne dokun.</span></li>
            <li className="flex items-start gap-2"><span className="mt-0.5 w-5 h-5 rounded-full text-[11px] font-semibold flex items-center justify-center" style={{ backgroundColor: "#dbe9ff", color: "#4992ff" }}>2</span><span><b>Uygulamayı yükle</b> ya da <b>Ana ekrana ekle</b> seçeneğine bas.</span></li>
            <li className="flex items-start gap-2"><span className="mt-0.5 w-5 h-5 rounded-full text-[11px] font-semibold flex items-center justify-center" style={{ backgroundColor: "#dbe9ff", color: "#4992ff" }}>3</span><span>Onayla — uygulama ikonu ana ekrana gelir.</span></li>
          </ol>
          <p className="text-xs mt-3" style={{ color: "hsl(var(--text-2))" }}>
            Eğer "Hemen Yükle" butonu üstte görüntülendiyse, doğrudan basabilirsin — Chrome senin için tek tıkla kuracak.
          </p>
        </div>
      )}
    </div>
  );
}

const ACTION_LABEL = {
  "auth.login": { txt: "Giriş yaptı", icon: ShieldCheck },
  "auth.change-password": { txt: "Şifresini değiştirdi", icon: ShieldCheck },
  "users.reset-password": { txt: "Kullanıcı şifresi sıfırladı", icon: ShieldCheck },
  "payments.create": { txt: "Ödeme ekledi", icon: Sailboat },
  "payments.update": { txt: "Ödeme güncelledi", icon: Sailboat },
  "payments.delete": { txt: "Ödeme sildi", icon: Sailboat },
  "shopping.done": { txt: "Hazır işaretledi", icon: CheckCircle2 },
  "shopping.undone": { txt: "Hazır işaretini kaldırdı", icon: CheckCircle2 },
  "cabins.update": { txt: "Kabin güncelledi", icon: Sailboat },
  "menu.update": { txt: "Menü güncelledi", icon: Sailboat },
  "menu.notes.update": { txt: "Menü notlarını güncelledi", icon: Sailboat },
  "actuals.create": { txt: "Maliyet ekledi", icon: Sailboat },
  "actuals.update": { txt: "Maliyet güncelledi", icon: Sailboat },
  "actuals.delete": { txt: "Maliyet sildi", icon: Sailboat },
};

function relTime(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} gün önce`;
  return d.toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function ActivityLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/audits", { params: { limit: 200 } });
      setItems(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="tk-card p-5" data-testid="activity-log-card">
      <div className="flex items-center gap-2 mb-3">
        <History className="w-5 h-5" style={{ color: "#4992ff" }} />
        <div className="font-display font-semibold">Son İşlemler</div>
        <span className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>(son 200 kayıt)</span>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm" style={{ color: "hsl(var(--text-2))" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...
        </div>
      ) : items.length === 0 ? (
        <div className="text-sm" style={{ color: "hsl(var(--text-2))" }}>Henüz kayıt yok.</div>
      ) : (
        <ul className="divide-y" style={{ borderColor: "hsl(var(--border))" }} data-testid="activity-list">
          {items.map((it, idx) => {
            const meta = ACTION_LABEL[it.action] || { txt: it.action, icon: Sailboat };
            const Icon = meta.icon;
            return (
              <li key={idx} className="flex items-start gap-3 py-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "hsl(var(--surface-2))" }}>
                  <Icon className="w-4 h-4" style={{ color: "#4992ff" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <b>{it.display_name}</b> <span style={{ color: "hsl(var(--text-2))" }}>{meta.txt}</span>
                    {it.target ? <> · <span className="text-[hsl(var(--text-1))]">{it.target}</span></> : null}
                  </div>
                  {it.details ? <div className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>{it.details}</div> : null}
                </div>
                <div className="text-[11px] flex-shrink-0" style={{ color: "hsl(var(--text-2))" }} title={new Date(it.ts).toLocaleString("tr-TR")}>
                  {relTime(it.ts)}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function Help() {
  return (
    <div className="space-y-5" data-testid="help-page">
      <div>
        <h1 className="text-2xl font-display font-semibold">Yardım</h1>
        <p className="text-sm" style={{ color: "hsl(var(--text-2))" }}>Uygulamayı telefonuna kur, son işlemlere göz at.</p>
      </div>

      <PwaInstall />
      <ActivityLog />
    </div>
  );
}
